// ValuationCard — lifecycle list item (PSD §10.3). Presentation.
//
// The featured variant (Stage 6e /polish) gives the active/most-recent
// valuation visual prominence on the Dashboard: full-width row, an accent
// left rule, and the common FMV promoted to a hero-scale figure while the
// supporting metrics move to a tight grid. The default variant stays a
// compact summary card so the rest of the list reads as secondary.

import { useStore } from '../../store/selectors';
import type { ValuationRecord } from '../../persistence/schema';
import { StatusBadge } from '../common/StatusBadge';
import { Tooltip } from '../common/Tooltip';
import { usdPrecise } from '../../format/format';

interface ValuationCardProps {
  record: ValuationRecord;
  featured?: boolean;
}

export function ValuationCard({ record, featured = false }: ValuationCardProps) {
  const setActive = useStore((s) => s.setActiveValuation);
  const navigate = useStore((s) => s.navigate);
  const fmv = record.result?.headline.commonFmvPerShare ?? null;
  return (
    <button
      type="button"
      className={`card card-button${featured ? ' card-featured valuation-card-featured' : ''}`}
      onClick={() => {
        setActive(record.valuationId);
        navigate('results');
      }}
      aria-label={`Open valuation for ${record.entityName}`}
    >
      <div className="card-header" style={{ minWidth: 0 }}>
        <strong
          title={record.entityName}
          style={{
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {record.entityName}
        </strong>
        <StatusBadge status={record.status} />
      </div>
      {featured ? (
        <div className="valuation-featured-body">
          <div className="metric valuation-featured-fmv">
            <span className="metric-label">
              Common FMV
              <Tooltip
                label="Fair market value per common share for this valuation, in USD. Null until the engine has run."
                ariaLabel="Help: Common FMV"
              />
            </span>
            <span className="metric-value metric-value-hero">
              {fmv !== null ? usdPrecise(fmv) : '—'}
            </span>
          </div>
          <div className="metric-grid-tight valuation-featured-meta">
            <div className="metric">
              <span className="metric-label">Valuation date</span>
              <span className="metric-value" style={{ fontSize: 'var(--font-size-2)' }}>
                {record.inputs.compliance.valuationDate}
              </span>
            </div>
            <div className="metric">
              <span className="metric-label">Safe harbor</span>
              <span className="metric-value" style={{ fontSize: 'var(--font-size-2)' }}>
                <span className={`badge badge-${record.inputs.compliance.safeHarbor === 'none' ? 'warning' : 'success'}`}>
                  {record.inputs.compliance.safeHarbor}
                </span>
              </span>
            </div>
          </div>
        </div>
      ) : (
        <div className="metric-grid-tight">
          <div className="metric">
            <span className="metric-label">Valuation date</span>
            <span className="metric-value" style={{ fontSize: 'var(--font-size-2)' }}>
              {record.inputs.compliance.valuationDate}
            </span>
          </div>
          <div className="metric">
            <span className="metric-label">
              Common FMV
              <Tooltip
                label="Fair market value per common share for this valuation, in USD. Null until the engine has run."
                ariaLabel="Help: Common FMV"
              />
            </span>
            <span className="metric-value">
              {fmv !== null ? usdPrecise(fmv) : '—'}
            </span>
          </div>
          <div className="metric">
            <span className="metric-label">Safe harbor</span>
            <span className="metric-value" style={{ fontSize: 'var(--font-size-2)' }}>
              <span className={`badge badge-${record.inputs.compliance.safeHarbor === 'none' ? 'warning' : 'success'}`}>
                {record.inputs.compliance.safeHarbor}
              </span>
            </span>
          </div>
        </div>
      )}
    </button>
  );
}
